// Dependency-free renderer for the MySportia / Exsportia legal content (legal-content.js).
// renderReact(React, doc, { linkComponent }) -> element tree; renderHtml(doc) -> string for static pages.
// Inline markup in the content: **bold** and [label](href). No raw HTML is ever interpreted.

const INLINE = /(\*\*[^*]+\*\*|\[[^\]]+\]\([^)\s]+\))/g;

export function parseInline(text) {
  const out = [];
  String(text)
    .split(INLINE)
    .forEach((part) => {
      if (!part) return;
      let m = /^\*\*([^*]+)\*\*$/.exec(part);
      if (m) return out.push({ type: 'strong', text: m[1] });
      m = /^\[([^\]]+)\]\(([^)\s]+)\)$/.exec(part);
      if (m) return out.push({ type: 'link', text: m[1], href: m[2] });
      out.push({ type: 'text', text: part });
    });
  return out;
}

const esc = (s) => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
const inlineHtml = (t) =>
  parseInline(t)
    .map((n) => (n.type === 'strong' ? `<strong>${esc(n.text)}</strong>` : n.type === 'link' ? `<a href="${esc(n.href)}">${esc(n.text)}</a>` : esc(n.text)))
    .join('');

function blockHtml(b) {
  switch (b.t) {
    case 'p': return `<p>${inlineHtml(b.x)}</p>`;
    case 'h3': return `<h3>${esc(b.x)}</h3>`;
    case 'note': return `<p class="legal-note">${inlineHtml(b.x)}</p>`;
    case 'ul': return `<ul>${b.items.map((i) => `<li>${inlineHtml(i)}</li>`).join('')}</ul>`;
    case 'ol': return `<ol>${b.items.map((i) => `<li>${inlineHtml(i)}</li>`).join('')}</ol>`;
    case 'table':
      return `<div class="legal-table-wrap"><table><thead><tr>${b.head.map((h) => `<th>${esc(h)}</th>`).join('')}</tr></thead><tbody>${b.rows.map((r) => `<tr>${r.map((c) => `<td>${inlineHtml(c)}</td>`).join('')}</tr>`).join('')}</tbody></table></div>`;
    default: return '';
  }
}

export function renderHtml(doc) {
  return [
    `<h1>${esc(doc.title)}</h1>`,
    `<p class="legal-meta">${esc(doc.meta)}</p>`,
    ...doc.lead.map(blockHtml),
    ...doc.sections.map((s) => `<section id="${esc(s.id)}"><h2>${esc(s.h)}</h2>${s.blocks.map(blockHtml).join('')}</section>`),
  ].join('\n');
}

export function renderReact(React, doc, opts = {}) {
  const h = React.createElement;
  const Link = opts.linkComponent || 'a';
  const inline = (t) =>
    parseInline(t).map((n, i) => {
      if (n.type === 'strong') return h('strong', { key: i }, n.text);
      if (n.type === 'link') {
        const external = /^(https?:|mailto:)/.test(n.href);
        return external || Link === 'a' ? h('a', { key: i, href: n.href }, n.text) : h(Link, { key: i, href: n.href }, n.text);
      }
      return n.text;
    });
  const block = (b, i) => {
    switch (b.t) {
      case 'p': return h('p', { key: i }, inline(b.x));
      case 'h3': return h('h3', { key: i }, b.x);
      case 'note': return h('p', { key: i, className: 'legal-note' }, inline(b.x));
      case 'ul': return h('ul', { key: i }, b.items.map((x, j) => h('li', { key: j }, inline(x))));
      case 'ol': return h('ol', { key: i }, b.items.map((x, j) => h('li', { key: j }, inline(x))));
      case 'table':
        return h('div', { key: i, className: 'legal-table-wrap' },
          h('table', null,
            h('thead', null, h('tr', null, b.head.map((c, j) => h('th', { key: j }, c)))),
            h('tbody', null, b.rows.map((r, j) => h('tr', { key: j }, r.map((c, k) => h('td', { key: k }, inline(c))))))));
      default: return null;
    }
  };
  return h('article', { className: 'legal-doc' },
    h('h1', null, doc.title),
    h('p', { className: 'legal-meta' }, doc.meta),
    doc.lead.map(block),
    doc.sections.map((s) => h('section', { key: s.id, id: s.id }, h('h2', null, s.h), s.blocks.map(block))));
}
